import { onCleanup, onMount } from "solid-js";
import type { BriefEditSection } from "../../enums/brief";
import { BriefEditForm } from "./BriefEditForm";
import type { DeliveryBrief } from "./briefTypes";

export function BriefEditSheet(props: {
  brief: DeliveryBrief;
  section: BriefEditSection;
  returnFocus: HTMLElement | null;
  onSave: (patch: Partial<DeliveryBrief>) => void;
  onClose: () => void;
}) {
  let panel: HTMLElement | undefined;
  let closeButton: HTMLButtonElement | undefined;
  onMount(() => queueMicrotask(() => closeButton?.focus()));
  onCleanup(() => queueMicrotask(() => props.returnFocus?.focus()));
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      props.onClose();
      return;
    }
    if (event.key !== "Tab" || !panel) return;
    const controls = focusableControls(panel);
    const first = controls[0];
    const last = controls.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  return (
    <div class="brief-sheet-layer">
      <button
        type="button"
        class="brief-sheet-scrim"
        aria-label="Close brief editor"
        onClick={props.onClose}
      />
      <aside
        ref={panel}
        class="brief-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="brief-edit-title"
        onKeyDown={onKeyDown}
      >
        <header>
          <div>
            <span class="signal-mini-kicker">Preview edit</span>
            <h2 id="brief-edit-title">Edit {sectionTitle(props.section)}</h2>
          </div>
          <button
            ref={closeButton}
            type="button"
            class="brief-sheet-close"
            aria-label="Close brief editor"
            title="Close brief editor"
            onClick={props.onClose}
          >
            x
          </button>
        </header>
        <div class="brief-sheet-body">
          <p class="brief-sheet-intro">
            This change is local to the current Preview session and is not
            saved.
          </p>
          <BriefEditForm
            brief={props.brief}
            section={props.section}
            onSave={(patch) => {
              props.onSave(patch);
              props.onClose();
            }}
            onCancel={props.onClose}
          />
        </div>
      </aside>
    </div>
  );
}

function sectionTitle(section: BriefEditSection): string {
  const labels: Record<BriefEditSection, string> = {
    identity: "brief details",
    objective: "objective",
    "required-change": "required change",
    scope: "scope",
    requirements: "requirements",
    acceptance: "acceptance criteria",
    questions: "open questions",
  };
  return labels[section];
}

function focusableControls(root: HTMLElement): HTMLElement[] {
  return [
    ...root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
}
